import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './styles/App.css';
import './components/Landing.css';
import { initGA, sendPageView } from './utils/analytics';
import { resetToHomeMeta } from './utils/ogMeta';
import { useLanguage } from './contexts/LanguageContext';
import { t } from './utils/i18n';

import Landing from './components/Landing';
import UserAuth from './components/UserAuth';
import BlogPost from './components/BlogPost';
import BlogAdmin from './components/BlogAdmin';
import AllNews from './components/AllNews';
import DomainCheckout from './components/DomainCheckout';
import ProductCheckout from './components/ProductCheckout';
import ProductPage from './components/ProductPage';

const API_BASE = process.env.REACT_APP_API_URL || '';

function App() {
  const { language } = useLanguage();
  const [user, setUser] = useState(null);
  const [authCheckDone, setAuthCheckDone] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [uiTheme, setUiTheme] = useState('aurora');
  const [uiSectionStyles, setUiSectionStyles] = useState({
    hero: 'default',
    offers: 'default',
    cta: 'default'
  });

  useEffect(() => {
    initGA();
  }, []);

  useEffect(() => {
    sendPageView(location.pathname + location.search);
    if (location.pathname === '/') {
      resetToHomeMeta();
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/config`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((cfg) => {
        if (cancelled || !cfg) return;
        setUiTheme(cfg.uiTheme || 'aurora');
        setUiSectionStyles({
          hero: cfg.uiSectionStyles?.hero || 'default',
          offers: cfg.uiSectionStyles?.offers || 'default',
          cta: cfg.uiSectionStyles?.cta || 'default'
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme-template', uiTheme || 'aurora');
  }, [uiTheme]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme-hero-layout', uiSectionStyles.hero || 'default');
    document.documentElement.setAttribute('data-theme-offers-layout', uiSectionStyles.offers || 'default');
    document.documentElement.setAttribute('data-theme-cta-layout', uiSectionStyles.cta || 'default');
  }, [uiSectionStyles]);

  useEffect(() => {
    if (location.pathname !== '/app/dashboard') {
      setAuthCheckDone(true);
      return;
    }
    let cancelled = false;
    let retryCount = 0;
    const MAX_RETRIES = 2;

    const checkAuth = () => {
      fetch(`${API_BASE}/auth/me`, { credentials: 'include', cache: 'no-store' })
        .then((r) => {
          if (cancelled) return;
          if (r.status === 401) {
            setUser(null);
            setAuthCheckDone(true);
            navigate('/user', { replace: true });
            return;
          }
          if (!r.ok) {
            setAuthCheckDone(true);
            return;
          }
          return r.json();
        })
        .then((data) => {
          if (cancelled) return;
          if (data) {
            setUser(data);
            setAuthCheckDone(true);
          } else {
            setAuthCheckDone(true);
          }
        })
        .catch((err) => {
          if (cancelled) return;
          if (err?.message === 'unauth' || err?.message?.includes('401')) {
            setUser(null);
            setAuthCheckDone(true);
            navigate('/', { replace: true });
            return;
          }
          const isGoogleRedirect = document.referrer.includes('accounts.google.com');
          if (isGoogleRedirect && retryCount < MAX_RETRIES) {
            retryCount++;
            setTimeout(() => {
              if (!cancelled) checkAuth();
            }, 500);
          } else {
            setUser(null);
            setAuthCheckDone(true);
            if (!isGoogleRedirect) {
              navigate('/user', { replace: true });
            }
          }
        });
    };

    const isGoogleRedirect = document.referrer.includes('accounts.google.com');
    if (isGoogleRedirect) {
      setTimeout(checkAuth, 200);
    } else {
      checkAuth();
    }

    return () => { cancelled = true; };
  }, [location.pathname, navigate]);

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/user" element={<UserAuth />} />
      <Route path="/checkout/domain" element={<DomainCheckout />} />
      <Route path="/checkout/product" element={<ProductCheckout />} />
      <Route path="/all-news" element={<AllNews />} />
      <Route path="/servicios/:slug" element={<ProductPage />} />
      <Route path="/services/:slug" element={<ProductPage />} />
      <Route path="/news/:categoryPath/:slug" element={<BlogPost />} />
      <Route path="/noticias/:categoryPath/:slug" element={<BlogPost />} />
      <Route
        path="/app/dashboard"
        element={
          location.pathname === '/app/dashboard' ? (
            authCheckDone && user ? (
              <BlogAdmin />
            ) : (
              <div className="landing-theme-dark app-route-loading-shell min-vh-100 d-flex flex-column align-items-center justify-content-center py-5">
                <div className="spinner-border text-light" role="status">
                  <span className="visually-hidden">{t('common.checkingAuth', language)}</span>
                </div>
                <p className="mt-3 app-route-loading-copy">{t('common.checkingAuth', language)}</p>
              </div>
            )
          ) : null
        }
      />
      <Route path="/app/blog-admin" element={<Navigate to="/app/dashboard" replace />} />
      <Route path="*" element={<Landing />} />
    </Routes>
  );
}

export default App;
